import type { HeaderFooter } from '../model/document';
import type { Section } from '../model/sections';
import { GOST_2_105, GOST_7_32 } from '../profiles/ids';
import { fmtNum, mmToTwips, twipsToMm } from '../util/units';
import { defineRule, type FixAction, type Rule, type RuleContext } from './types';
import { ALIGN_RU, mainTextSize } from './util';

const SIDES = ['top', 'bottom', 'left', 'right'] as const;
type Side = (typeof SIDES)[number];
const SIDE_RU: Record<Side, string> = { top: 'верхнее', bottom: 'нижнее', left: 'левое', right: 'правое' };

const sectionPrefix = (ctx: RuleContext, sec: Section): string =>
  ctx.model.sections.length > 1 ? `Раздел документа ${sec.index + 1}: ` : '';

/** Колонтитул раздела с номером страницы (сначала нижний). */
function numberedHf(ctx: RuleContext, sec: Section): HeaderFooter | undefined {
  for (const path of [sec.footers.default, sec.headers.default, sec.footers.even, sec.headers.even]) {
    const hf = path ? ctx.model.headersFooters.get(path) : undefined;
    if (hf?.pageField) return hf;
  }
  return undefined;
}

export const pageRules: Rule[] = [
  defineRule({
    id: 'page.size',
    category: 'page',
    title: 'Формат листа А4',
    severity: 'error',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.1.1', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.3.6, 8.9' },
    why: {
      [GOST_7_32]: 'Страницы отчёта должны соответствовать формату А4 (210 × 297 мм). А3 допускается только для крупных таблиц и иллюстраций.',
      [GOST_2_105]: 'Документ выполняют на листах формата А4 (210 × 297 мм); для приложений допускаются А3 и более крупные форматы.',
    },
    check(ctx) {
      const W = mmToTwips(ctx.profile.page.widthMm);
      const H = mmToTwips(ctx.profile.page.heightMm);
      const tol = mmToTwips(2);
      for (const sec of ctx.model.sections) {
        if (sec.pageWidth === undefined || sec.pageHeight === undefined) continue;
        const w = Math.min(sec.pageWidth, sec.pageHeight);
        const h = Math.max(sec.pageWidth, sec.pageHeight);
        if (Math.abs(w - W) <= tol && Math.abs(h - H) <= tol) continue;
        const a3 = Math.abs(w - mmToTwips(297)) <= tol && Math.abs(h - mmToTwips(420)) <= tol;
        ctx.report({
          severity: a3 ? 'info' : undefined,
          paragraph: sec.firstParagraph,
          message:
            `${sectionPrefix(ctx, sec)}размер листа ${fmtNum(twipsToMm(w), 0)} × ${fmtNum(twipsToMm(h), 0)} мм` +
            (a3 ? ' — формат А3 допускается только для крупных таблиц и иллюстраций' : ' — нужен А4 (210 × 297 мм)'),
          fix: a3 ? undefined : [{ kind: 'section', section: sec.index, widthTw: sec.landscape ? H : W, heightTw: sec.landscape ? W : H }],
          fixLabel: 'Установить А4',
        });
      }
    },
  }),

  defineRule({
    id: 'page.margins',
    category: 'page',
    title: 'Поля страницы',
    severity: 'error',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.1.1', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 5.1.3' },
    why: {
      [GOST_7_32]: 'Левое поле — 30 мм, правое — 15 мм, верхнее и нижнее — 20 мм.',
      [GOST_2_105]: 'Стандарт задаёт расстояние от рамки до текста: не менее 3 мм по бокам и 10 мм сверху и снизу. Для работ без рамки поля берут из методички — обычно левое 30 мм, правое 15 мм, верхнее и нижнее 20 мм.',
    },
    check(ctx) {
      const want = ctx.profile.page.margins;
      const tol = ctx.profile.page.toleranceMm;
      const gutterTop = ctx.model.settings.gutterAtTop;
      for (const sec of ctx.model.sections) {
        const m = sec.margins;
        if (m.top === undefined || m.bottom === undefined || m.left === undefined || m.right === undefined) continue;
        const gutter = m.gutter ?? 0;
        const actual: Record<Side, number> = {
          top: twipsToMm(m.top + (gutterTop ? gutter : 0)),
          bottom: twipsToMm(m.bottom),
          left: twipsToMm(m.left + (gutterTop ? 0 : gutter)),
          right: twipsToMm(m.right),
        };
        // Для альбомных страниц поле под переплёт оказывается сверху или снизу.
        const variants: Array<Record<Side, number>> = [want];
        if (sec.landscape) {
          variants.unshift({ top: want.left, bottom: want.right, left: want.top, right: want.bottom });
          variants.push({ top: want.right, bottom: want.left, left: want.bottom, right: want.top });
        }
        if (variants.some((v) => SIDES.every((k) => Math.abs(actual[k] - v[k]) <= tol))) continue;
        const target = variants[0]!;
        const wrong = SIDES.filter((k) => Math.abs(actual[k] - target[k]) > tol);
        ctx.report({
          paragraph: sec.firstParagraph,
          message:
            `${sectionPrefix(ctx, sec)}` +
            wrong.map((k) => `${SIDE_RU[k]} поле ${fmtNum(actual[k], 1)} мм (нужно ${fmtNum(target[k], 1)})`).join(', ') +
            (gutter ? '; учтено поле переплёта' : ''),
          fix: [
            {
              kind: 'section',
              section: sec.index,
              margins: {
                top: mmToTwips(target.top),
                bottom: mmToTwips(target.bottom),
                left: mmToTwips(target.left),
                right: mmToTwips(target.right),
                ...(gutter ? { gutter: 0 } : {}),
              },
            },
          ],
          fixLabel: 'Выставить поля',
        });
      }
    },
  }),

  defineRule({
    id: 'page.numbers',
    category: 'page',
    title: 'Нумерация страниц',
    severity: 'error',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.3.1', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.3.7, 6.5.10' },
    why: {
      [GOST_7_32]: 'Страницы нумеруют арабскими цифрами, сквозной нумерацией по всему тексту, включая приложения.',
      [GOST_2_105]: 'Страницы документа и приложений нумеруют сквозной нумерацией.',
    },
    check(ctx) {
      if ([...ctx.model.headersFooters.values()].some((hf) => hf.pageField)) return;
      const { position, align, hideOnTitle } = ctx.profile.pageNumbers;
      const fix: FixAction[] = [{ kind: 'pageNumbers', position, align, font: ctx.profile.text.fonts[0], sz: mainTextSize(ctx) }];
      if (hideOnTitle && ctx.s.hasTitlePage && !ctx.model.sections[0]?.titlePg) fix.push({ kind: 'titlePage', section: 0 });
      ctx.report({
        message: `В документе нет номеров страниц. Вставьте их: «Вставка → Номер страницы → ${position === 'top' ? 'Вверху' : 'Внизу'} страницы»`,
        fix,
        fixLabel: 'Вставить номера страниц',
      });
    },
  }),

  defineRule({
    id: 'page.numbers-position',
    category: 'page',
    title: 'Положение номера страницы',
    severity: 'warning',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.3.1', [GOST_2_105]: '' },
    why: 'По ГОСТ 7.32 номер страницы проставляют в центре нижней части страницы без точки; некоторые методички требуют другое положение — это меняется в настройках.',
    check(ctx) {
      const { position, align } = ctx.profile.pageNumbers;
      const seen = new Set<string>();
      for (const sec of ctx.model.sections) {
        const hf = numberedHf(ctx, sec);
        if (!hf || seen.has(hf.part)) continue;
        seen.add(hf.part);
        const pf = hf.pageField!;
        const where = hf.kind === 'footer' ? 'bottom' : 'top';
        const problems: string[] = [];
        if (where !== position) problems.push(position === 'bottom' ? 'стоит вверху, а нужно внизу' : 'стоит внизу, а нужно вверху');
        const alignOk = pf.align === align || pf.align === 'unknown';
        if (!alignOk) problems.push(`выровнен ${ALIGN_RU[pf.align]}, а нужно ${ALIGN_RU[align]}`);
        const text = pf.paragraph.text.trim();
        if (/^\d+\s*\.$|^[-–—]\s*\d+\s*[-–—]$|стр/i.test(text)) problems.push(`записан как «${text}» — нужна только цифра, без точки и тире`);
        if (!problems.length) continue;
        ctx.report({
          message: `Номер страницы ${problems.join('; ')}`,
          fix: !alignOk && !pf.inTextBox ? [{ kind: 'hfAlign', part: hf.part, index: pf.paragraph.index, jc: align }] : undefined,
          fixLabel: 'Выровнять номер',
        });
      }
    },
  }),

  defineRule({
    id: 'page.numbers-title',
    category: 'page',
    title: 'Без номера на титульном листе',
    severity: 'warning',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.3.2', [GOST_2_105]: '' },
    why: 'Титульный лист включают в общую нумерацию, но номер на нём не проставляют.',
    check(ctx) {
      if (!ctx.profile.pageNumbers.hideOnTitle || !ctx.s.hasTitlePage) return;
      const sec = ctx.model.sections[0];
      if (!sec) return;
      if (!sec.titlePg) {
        if (numberedHf(ctx, sec)) {
          ctx.report({
            paragraph: 0,
            message: 'На титульном листе печатается номер страницы. Включите «Особый колонтитул для первой страницы»',
            fix: [{ kind: 'titlePage', section: 0 }],
            fixLabel: 'Скрыть номер на титуле',
          });
        }
        return;
      }
      const first = sec.footers.first ?? sec.headers.first;
      const hf = first ? ctx.model.headersFooters.get(first) : undefined;
      if (hf?.pageField) {
        ctx.report({ paragraph: 0, message: 'Колонтитул первой страницы содержит номер — на титульном листе его быть не должно' });
      }
    },
  }),

  defineRule({
    id: 'page.numbers-continuous',
    category: 'page',
    title: 'Сквозная нумерация',
    severity: 'warning',
    gost: { [GOST_7_32]: 'ГОСТ 7.32-2017, п. 6.3.1', [GOST_2_105]: 'ГОСТ Р 2.105-2019, п. 6.5.10' },
    why: 'Нумерация сквозная по всему документу арабскими цифрами — без перезапуска в разделах и римских цифр.',
    check(ctx) {
      for (const sec of ctx.model.sections) {
        const problems: string[] = [];
        if (sec.index > 0 && sec.pgNumStart !== undefined) problems.push(`нумерация начинается заново с ${sec.pgNumStart}`);
        if (sec.pgNumFmt && sec.pgNumFmt !== 'decimal') problems.push('номера не арабскими цифрами');
        if (!problems.length) continue;
        ctx.report({
          paragraph: sec.firstParagraph,
          message: `${sectionPrefix(ctx, sec)}${problems.join(', ')}`,
          fix: [{ kind: 'section', section: sec.index, clearPageRestart: true }],
          fixLabel: 'Сделать нумерацию сквозной',
        });
      }
    },
  }),
];
